import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type { CART_TYPE } from '~/hooks/useCartTypeParam';
import type { OAISchema } from '~/types/openapi';

// Response 타입 (shoebill.ts에서 가져옴)
type BuyerCouponListResponse = OAISchema['MarketBuyerCouponResponse'][];
type BuyerCouponCountResponse = number;
type BuyerCouponDetailResponse = OAISchema['MarketBuyerCouponDetailResponse'];
type CheckoutCouponResponse = OAISchema['MarketCheckoutCouponResponse'];
type DownloadedCouponResponse = OAISchema['MarketDownloadCouponResponseV2'][];
type PointCouponListResponse = OAISchema['MarketPlacePointCouponDto'][];
type CouponDetailResponse = OAISchema['DetailCouponResponse'];

export interface CouponsDataSource {
  // Query
  getBuyerCoupons(
    params?: Record<string, unknown>,
  ): Promise<BuyerCouponListResponse>;
  getBuyerCouponCount(): Promise<BuyerCouponCountResponse>;
  getBuyerCouponDetail(
    buyerCouponId: number,
  ): Promise<BuyerCouponDetailResponse>;
  getCheckoutCoupons(cartType: CART_TYPE): Promise<CheckoutCouponResponse>;
  getDownloadedCoupons(couponIds: number[]): Promise<DownloadedCouponResponse>;
  getPointCouponList(): Promise<PointCouponListResponse>;
  getCouponDetail(couponId: number): Promise<CouponDetailResponse>;

  // Mutation
  downloadAllCoupons(couponIds: number[]): Promise<void>;
  downloadCoupon(couponId: number): Promise<void>;
  issuePointCoupon(couponId: number): Promise<void>;
}

export class CouponsDataSourceImpl implements CouponsDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getBuyerCoupons(params?: Record<string, unknown>) {
    return this.client.request<BuyerCouponListResponse>({
      url: '/api/market-place/v1/buyer-coupon',
      method: 'get',
      params,
    });
  }

  async getBuyerCouponCount() {
    return this.client.request<BuyerCouponCountResponse>({
      url: '/api/market-place/v1/buyer-coupon/count',
      method: 'get',
    });
  }

  async getBuyerCouponDetail(buyerCouponId: number) {
    return this.client.request<BuyerCouponDetailResponse>({
      url: `/api/market-place/v1/buyer-coupon/${buyerCouponId}`,
      method: 'get',
    });
  }

  async getCheckoutCoupons(cartType: CART_TYPE) {
    return this.client.request<CheckoutCouponResponse>({
      url: '/api/market-place/v1/shopping-cart/checkout/coupon',
      method: 'get',
      params: { cartType },
    });
  }

  async getDownloadedCoupons(couponIds: number[]) {
    return this.client.request<DownloadedCouponResponse>({
      url: '/api/market-place/v2/coupon/download',
      method: 'get',
      params: { couponIds },
    });
  }

  async getPointCouponList() {
    return this.client.request<PointCouponListResponse>({
      url: '/api/market-place/v1/coupon/point',
      method: 'get',
    });
  }

  async getCouponDetail(couponId: number) {
    return this.client.request<CouponDetailResponse>({
      url: `/api/market-place/v1/coupon/${couponId}`,
      method: 'get',
    });
  }

  async downloadAllCoupons(couponIds: number[]): Promise<void> {
    await this.client.request<void>({
      url: '/api/market-place/v1/coupon/download/issue',
      method: 'post',
      data: { couponIds },
    });
  }

  async downloadCoupon(couponId: number): Promise<void> {
    await this.client.request<void>({
      url: `/api/market-place/v1/coupon/download/${couponId}/issue`,
      method: 'post',
    });
  }

  async issuePointCoupon(couponId: number): Promise<void> {
    await this.client.request<void>({
      url: `/api/market-place/v1/coupon/point/${couponId}/issue`,
      method: 'post',
    });
  }
}

export const couponsDataSource = new CouponsDataSourceImpl();
